import { useCallback } from 'react'

import { Clear as ClearIcon } from '@mui/icons-material'
import { Autocomplete as MuiAutocomplete, TextField } from '@mui/material'
import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { BulkAutocompleteProps, BulkAutocompleteValue } from './types'

import { useInputValue } from './hooks/useInputValue'

const defaultComponentValue = {
  status: 'mixed',
}

export function BulkAutocomplete<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkAutocompleteValue,
>(props: BulkAutocompleteProps<TargetField, State, AcceptedFormValue>) {
  const {
    label,
    placeholder,
    helperText,
    options: autocompleteOptions,
    validateOn = 'change',
    testId,
    freeSolo = false,
    disabled = false,
    initialValue = defaultComponentValue,
    mixedLabel,
    ...rest
  } = props

  const { fieldInfo } = useTidalFormBridge<
    BulkAutocompleteValue,
    TargetField,
    State,
    AcceptedFormValue
  >({
    validateOn,
    ...rest,
  })

  const isMixedValue = fieldInfo.value.status === 'mixed'
  const valueAsString = fieldInfo.value.status === 'mixed' ? '' : fieldInfo.value.value

  const onChange = useCallback(
    (_, newValue: string | null) => {
      // changeField(newValue ?? '')
      // when value changes from outside (e.g. form update the initial values)
      // the inner callbacks will call the change with the same value
      // since the value is an object a new reference will be generate
      // to preserve the `pristine` status this has to be handled
      const value = newValue ?? ''
      if (fieldInfo.value.status === 'exact' && fieldInfo.value.value === value) return

      fieldInfo.onChange({ status: 'exact', value })
    },
    [fieldInfo.onChange, fieldInfo.value],
  )
  const { inputValue, onInputChange } = useInputValue(onChange)

  return (
    <MuiAutocomplete
      freeSolo
      openOnFocus
      disabled={disabled || fieldInfo.fieldDisabled}
      value={isMixedValue ? '' : valueAsString}
      data-testid={testId}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={onInputChange}
      clearIcon={<ClearIcon />}
      options={autocompleteOptions}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          error={fieldInfo.fieldHasError}
          helperText={fieldInfo.errorOrHelperText || helperText}
          data-testid={testId}
          fullWidth
          placeholder={isMixedValue ? mixedLabel : placeholder}
          InputLabelProps={{
            shrink:
              isMixedValue || (fieldInfo.value.status === 'exact' && valueAsString.length > 0),
          }}
        />
      )}
    />
  )
}
